import React, { useEffect, useRef, useState } from "react";
import { ChoiceButton, ProgressPill, useShuffledChoices } from "../grammar/shared";
import { fetchOnboardingAudioManifest, onboardingAudioUrl } from "../../api";
import { track } from "../../utils/analytics";
import { duffFeedback } from "../../utils/duff";
import { HOOK_QUIZ } from "./onboardingContent";

/**
 * OnboardingHookQuiz — the funnel's opening screen: instead of telling the
 * visitor "you can already read Arabic", a 30-second guess-the-word quiz
 * lets them prove it to themselves. Three winnable high-frequency Quranic
 * words; wrong answers never sting — every reveal lands on "you're closer
 * than you think". Reuses the grammar activities' quiz primitives so it
 * feels like the app's real quizzes.
 *
 * Sub-flow: intro → word 0..2 (answer → feedback → next) → result → onDone.
 */
export default function OnboardingHookQuiz({ onDone }) {
  const [idx, setIdx] = useState(-1); // -1 = intro, HOOK_QUIZ.length = result
  const [picked, setPicked] = useState(null); // shuffled index of the tap
  const [score, setScore] = useState(0);
  const [audioKeys, setAudioKeys] = useState(null); // Set | null = no TTS
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchOnboardingAudioManifest()
      .then((m) => {
        if (m?.enabled && Array.isArray(m.keys)) setAudioKeys(new Set(m.keys));
      })
      .catch(() => {});
    return () => audioRef.current?.pause();
  }, []);

  const item = idx >= 0 && idx < HOOK_QUIZ.length ? HOOK_QUIZ[idx] : null;
  // Convention: choices authored correct-first; shuffle per word.
  const view = useShuffledChoices(item ? item.choices : [], 0, [idx]);
  const answered = picked != null;
  const wasCorrect = answered && picked === view.correctIndex;

  const playWord = (i) => {
    const key = HOOK_QUIZ[i]?.audioKey;
    if (!key || !audioKeys?.has(key)) return;
    try {
      audioRef.current?.pause();
      const audio = new Audio(onboardingAudioUrl(key));
      audioRef.current = audio;
      setPlaying(true);
      audio.addEventListener("ended", () => setPlaying(false));
      audio.play().catch(() => setPlaying(false));
    } catch {
      setPlaying(false);
    }
  };

  const pick = (i) => {
    if (answered) return;
    setPicked(i);
    const correct = i === view.correctIndex;
    if (correct) setScore((s) => s + 1);
    duffFeedback(correct ? "correct" : "wrong");
    track("onboarding-quiz-answer", { word: item.key, correct });
  };

  // Word audio plays from inside the click handlers (not an effect) so it
  // stays within the browser's user-gesture allowance for autoplay.
  const start = () => {
    setIdx(0);
    playWord(0);
  };

  const next = () => {
    setPicked(null);
    if (idx + 1 >= HOOK_QUIZ.length) {
      track("onboarding-quiz-result", { score });
    } else {
      playWord(idx + 1);
    }
    setIdx((i) => i + 1);
  };

  // ── Intro ────────────────────────────────────────────────────────────
  if (idx === -1) {
    return (
      <div className="w-full text-center pt-6">
        <p dir="rtl" lang="ar" className="font-amiri text-4xl text-dt-gold-bright leading-relaxed mb-5 select-none">
          وَلَقَدْ يَسَّرْنَا الْقُرْآنَ لِلذِّكْرِ
        </p>
        <h1 className="text-2xl font-semibold text-dt-parchment mb-3">
          You can already read the Quran's words
        </h1>
        <p className="text-sm text-dt-parchment-dim mb-8 max-w-sm mx-auto">
          Don't believe it? Three words, thirty seconds — see how much Quranic
          Arabic you already carry from your daily prayers.
        </p>
        <button
          type="button"
          onClick={start}
          className="w-full max-w-sm px-4 py-3.5 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-[15px] font-semibold transition-[filter]"
        >
          Try 3 words — بِسْمِ الله
        </button>
      </div>
    );
  }

  // ── Result ───────────────────────────────────────────────────────────
  if (!item) {
    const headline =
      score === 3
        ? "You already read the Quran's words"
        : score === 2
          ? "You're closer than you think"
          : "The perfect starting point";
    const body =
      score >= 2
        ? "Those are among the Quran's most frequent words — and you just read them. Murājaʿa turns that head start into real understanding, a few minutes a day."
        : "Those three are among the Quran's most frequent words — they'll be yours within your first week. A few minutes a day is all it takes.";
    return (
      <div className="w-full text-center pt-6 animate-rise">
        <p className="font-display text-5xl text-dt-gold-bright mb-3 select-none">{score}/3</p>
        <h1 className="text-2xl font-semibold text-dt-parchment mb-3">{headline}</h1>
        <p className="text-sm text-dt-parchment-dim mb-8 max-w-sm mx-auto">{body}</p>
        <button
          type="button"
          onClick={onDone}
          className="w-full max-w-sm px-4 py-3.5 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-[15px] font-semibold transition-[filter]"
        >
          Build my plan
        </button>
      </div>
    );
  }

  // ── One word ─────────────────────────────────────────────────────────
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-dt-parchment">What does this word mean?</h2>
        <ProgressPill>{idx + 1}/{HOOK_QUIZ.length}</ProgressPill>
      </div>

      <div className="surface rounded-xl px-4 py-6 text-center mb-4 relative">
        <p dir="rtl" lang="ar" className="font-amiri text-5xl text-dt-parchment leading-relaxed select-none">
          {item.arabic}
        </p>
        <p className="text-xs text-dt-parchment-dim italic mt-2">{item.translit}</p>
        {audioKeys?.has(item.audioKey) && (
          <button
            type="button"
            onClick={() => playWord(idx)}
            aria-label="Hear the word again"
            className="absolute top-2.5 right-3 text-base transition-colors"
            style={{ color: playing ? "var(--gold-bright)" : "var(--parchment-dim)" }}
          >
            {playing ? "▮▮" : "🔊"}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2.5">
        {view.choices.map((choice, i) => {
          let state = "idle";
          if (answered) {
            if (i === view.correctIndex) state = "correct";
            else if (i === picked) state = "wrong";
            else state = "dim";
          }
          return (
            <ChoiceButton key={choice} state={state} disabled={answered} onClick={() => pick(i)}>
              {choice}
            </ChoiceButton>
          );
        })}
      </div>

      {answered && (
        <div className="mt-4 animate-rise">
          <p className="text-sm text-dt-parchment">
            {wasCorrect ? "✓ Exactly." : `It means “${item.choices[0]}.”`}
          </p>
          <p className="text-xs text-dt-parchment-dim mt-1">
            {wasCorrect ? item.fact : item.encourage}
          </p>
          <button
            type="button"
            onClick={next}
            className="w-full mt-4 px-4 py-3 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter]"
          >
            {idx + 1 < HOOK_QUIZ.length ? "Next word" : "See my result"}
          </button>
        </div>
      )}
    </div>
  );
}
